import type { KeyboardEvent, ReactNode } from "react";
import { TripCard, tripColorFor, vars } from "@pack-it-up/ui-web";
import type { TripSummary } from "@pack-it-up/core";
import { PlusIcon } from "../../app/icons.js";
import { countdown, fmtTripDates } from "../../lib/format.js";

// Dashed "New Trip" card at the end of a trip grid.
export const NewTripCard = ({ onClick }: { onClick: () => void }): ReactNode => {
  const onKeyDown = (e: KeyboardEvent): void => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      onClick();
    }
  };
  return (
    <div
      role="button"
      tabIndex={0}
      aria-label="New trip"
      onClick={onClick}
      onKeyDown={onKeyDown}
      style={{
        border: `1px dashed ${vars.color.border}`,
        borderRadius: vars.radius.lg,
        minHeight: "148px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexDirection: "column",
        gap: vars.space.xs,
        opacity: 0.7,
        cursor: "pointer",
        transition: "opacity 150ms ease, border-color 150ms ease, background-color 150ms ease",
      }}
      onMouseEnter={(e) => {
        (e.currentTarget as HTMLDivElement).style.opacity = "1";
        (e.currentTarget as HTMLDivElement).style.borderColor = vars.color.accent;
        (e.currentTarget as HTMLDivElement).style.backgroundColor = vars.color.accentSubtle;
      }}
      onMouseLeave={(e) => {
        (e.currentTarget as HTMLDivElement).style.opacity = "0.7";
        (e.currentTarget as HTMLDivElement).style.borderColor = vars.color.border;
        (e.currentTarget as HTMLDivElement).style.backgroundColor = "transparent";
      }}
    >
      {PlusIcon(18)}
      <span style={{ fontSize: vars.font.sizeSm, color: vars.color.textMuted, fontWeight: vars.font.weightMedium }}>
        New Trip
      </span>
    </div>
  );
};

// The trip grid — color-coded cards + an optional dashed new-trip card at the end.
// Shared by Home (upcoming) and My Trips (upcoming/past).
export const TripGrid = ({
  trips,
  onOpen,
  onNew,
}: {
  trips: TripSummary[];
  onOpen: (id: string) => void;
  onNew?: () => void;
}): ReactNode => (
  <div
    style={{
      display: "grid",
      gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
      gap: vars.space.md,
      marginBottom: vars.space.xl,
    }}
  >
    {trips.map((t) => (
      <TripCard
        key={t.id}
        name={t.name}
        color={t.color ?? tripColorFor(t.id)}
        meta={fmtTripDates(t)}
        {...(countdown(t.startDate, t.endDate) !== null ? { countdown: countdown(t.startDate, t.endDate)! } : {})}
        {...(t.location !== undefined ? { location: t.location } : {})}
        people={(t.people ?? []).map((p) => ({ name: p.displayName }))}
        onClick={() => onOpen(t.id)}
      />
    ))}
    {onNew !== undefined && <NewTripCard onClick={onNew} />}
  </div>
);
